import PublicLayout from '@/layouts/PublicLayout'
import SeoHead from '@/components/SeoHead'

export default function CookiesPage() {
  return (
    <>
      <SeoHead
        title="Cookie Policy — LendSolo"
        description="How LendSolo uses cookies on lendsolo.com."
        canonicalPath="/cookies"
        ogType="article"
      />

      <PublicLayout
        title="Cookie Policy"
        description="Last updated April 20, 2026"
      >
        <div
          className="prose prose-gray max-w-none
            prose-headings:font-bold prose-headings:text-gray-900 prose-headings:scroll-mt-16
            prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4
            prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3
            prose-p:leading-relaxed prose-p:text-gray-700
            prose-li:text-gray-700
            prose-a:text-emerald-600 prose-a:font-medium hover:prose-a:text-emerald-700
            prose-strong:text-gray-900"
        >
          <p>
            This Cookie Policy explains how Lendsolo LLC ("<strong>Company</strong>,"
            "<strong>we</strong>," "<strong>us</strong>," and "<strong>our</strong>") uses
            cookies when you visit our website at{' '}
            <a href="https://www.lendsolo.com" target="_blank" rel="noopener noreferrer">
              https://www.lendsolo.com
            </a>{' '}
            ("<strong>Website</strong>").
          </p>

          <h2>What are cookies?</h2>
          <p>
            Cookies are small data files that are placed on your computer or mobile device
            when you visit a website. Cookies are widely used by website owners to make their
            websites work, or to work more efficiently, as well as to provide basic
            functionality.
          </p>
          <p>
            Cookies set by the website owner (in this case, Lendsolo LLC) are called
            "first-party cookies." Cookies set by parties other than the website owner are
            called "third-party cookies."
          </p>

          <h2>What cookies do we use?</h2>
          <p>
            LendSolo uses only essential, first-party cookies required for the website and
            application to function. We do not use cookies for analytics, advertising,
            tracking, or profiling. We do not set third-party marketing or advertising
            cookies.
          </p>
          <p>The specific cookies we set are:</p>

          <h3>Session cookie</h3>
          <ul>
            <li><strong>Name:</strong> <code>_lendsolo_session</code></li>
            <li>
              <strong>Purpose:</strong> Keeps you signed in as you navigate the application
              and carries the CSRF token used to protect requests. Required for authentication
              and security.
            </li>
            <li><strong>Provider:</strong> lendsolo.com (first-party)</li>
            <li><strong>Type:</strong> Session cookie</li>
            <li><strong>Expires:</strong> When you close your browser or sign out</li>
          </ul>

          <h3>Remember-me cookies</h3>
          <ul>
            <li>
              <strong>Names:</strong> <code>remember_user_token</code>,{' '}
              <code>remember_team_member_token</code>
            </li>
            <li>
              <strong>Purpose:</strong> Keeps you signed in across browser sessions when you
              check "Remember me" at login. Not set unless you opt in.
            </li>
            <li><strong>Provider:</strong> lendsolo.com (first-party)</li>
            <li><strong>Type:</strong> Persistent cookie</li>
            <li><strong>Expires:</strong> After 2 weeks, or when you sign out</li>
          </ul>

          <h3>Payment processing (Stripe)</h3>
          <p>
            When you subscribe or manage billing, you may be redirected to Stripe-hosted
            Checkout or Customer Portal pages. Stripe sets its own cookies on those pages
            under the <code>stripe.com</code> domain. These are governed by Stripe's own
            cookie and privacy policies, available at{' '}
            <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer">
              stripe.com/privacy
            </a>
            . Stripe does not set cookies on lendsolo.com.
          </p>

          <h2>How can I control cookies?</h2>
          <p>
            The cookies we set are strictly necessary for the website and application to
            function (authentication, security). Rejecting these cookies will prevent you
            from signing in or completing key actions.
          </p>
          <p>
            You can manage or delete cookies through your browser settings at any time. The
            following links explain how to do this in the most popular browsers:
          </p>
          <ul>
            <li>
              <a
                href="https://support.google.com/chrome/answer/95647#zippy=%2Callow-or-block-cookies"
                target="_blank"
                rel="noopener noreferrer"
              >
                Chrome
              </a>
            </li>
            <li>
              <a
                href="https://support.mozilla.org/en-US/kb/enhanced-tracking-protection-firefox-desktop"
                target="_blank"
                rel="noopener noreferrer"
              >
                Firefox
              </a>
            </li>
            <li>
              <a
                href="https://support.apple.com/en-ie/guide/safari/sfri11471/mac"
                target="_blank"
                rel="noopener noreferrer"
              >
                Safari
              </a>
            </li>
            <li>
              <a
                href="https://support.microsoft.com/en-us/windows/microsoft-edge-browsing-data-and-privacy-bb8174ba-9d73-dcf2-9b4a-c582b4e640dd"
                target="_blank"
                rel="noopener noreferrer"
              >
                Edge
              </a>
            </li>
            <li>
              <a
                href="https://help.opera.com/en/latest/web-preferences/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Opera
              </a>
            </li>
          </ul>

          <h2>Do we use other tracking technologies?</h2>
          <p>
            No. LendSolo does not use web beacons, tracking pixels, fingerprinting, or any
            other tracking technologies.
          </p>

          <h2>Do we serve targeted advertising?</h2>
          <p>
            No. LendSolo does not run advertising and does not allow third parties to serve
            ads or tracking cookies on our website.
          </p>

          <h2>How often will we update this Cookie Policy?</h2>
          <p>
            We may update this Cookie Policy from time to time to reflect changes to the
            cookies we use or for other operational, legal, or regulatory reasons. The date
            at the top of this Cookie Policy indicates when it was last updated.
          </p>

          <h2>Questions?</h2>
          <p>
            If you have any questions about our use of cookies, email us at{' '}
            <a href="mailto:support@lendsolo.com">support@lendsolo.com</a>.
          </p>
        </div>
      </PublicLayout>
    </>
  )
}
